import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   Button,
   Stack,
   TableContainer,
   Table,
   Textarea,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
} from '@chakra-ui/react';
import {
   ColumnDef,
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { _js } from '@ifixit/localize';
import { color, space } from '@ifixit/primitives';
import { EditableCell, useTable, ActionButton, type TableRow } from './Table';

declare global {
   interface Window {
      WikiSuppliers: {
         getFormValues: () => SupplierRow[];
      };
   }
}

type SupplierColumn = {
   property: keyof SupplierRow;
   label: string;
};

type SupplierRow = {
   'part_#': string;
   supplier: string;
   type: string;
   url: string;
};

type SupplierTableRow = TableRow<SupplierRow>;

const columnHelper = createColumnHelper<SupplierTableRow>();

function SuppliersEdit(props: { columns: SupplierColumn[]; rows: SupplierRow[] }) {
   const { data, setData, deleteRow, addRow, editCell } = useTable<SupplierRow>(props.rows);

   const [bulkAddText, setBulkAddText] = useState('');

   const bulkAdd = () => {
      const urls = bulkAddText.split('\n').map(url => url.trim());
      bulkAddUrlRows(urls);
      setBulkAddText('');
   };

   const bulkAddUrlRows = (urls: string[]) => {
      const urlRows = urls.map(
         (newUrl: string): SupplierTableRow => ({
            'part_#': '',
            supplier: '',
            type: '',
            url: newUrl,
            action: 'delete',
         })
      );

      setData(oldData => {
         const newRowRow = oldData.pop();
         return oldData.concat(urlRows, newRowRow ? [newRowRow] : []);
      });
   };

   const defaultColumn: Partial<ColumnDef<SupplierTableRow>> = {
      cell: cellProps => (
         <EditableCell
            cellProps={cellProps}
            onSubmit={(newValue: string) => editCell(cellProps.cell, newValue)}
         />
      ),
   };

   const columns: ColumnDef<SupplierTableRow, string>[] = useMemo(
      () => [
         columnHelper.accessor('type', {
            header: _js('Type'),
         }),
         columnHelper.accessor('part_#', {
            header: _js('Part #'),
         }),
         columnHelper.accessor('supplier', {
            header: _js('Supplier'),
         }),
         columnHelper.accessor('url', {
            header: _js('Url'),
         }),
         columnHelper.display({
            id: 'actions',
            cell: ({ row }) => {
               const enabled =
                  row.original['part_#'] ||
                  row.original.supplier ||
                  row.original.type ||
                  row.original.url;

               return (
                  <ActionButton
                     action={row.original.action}
                     rowIdx={row.index}
                     deleteRow={deleteRow}
                     addRow={addRow}
                     addDisabled={!enabled}
                  />
               );
            },
         }),
      ],
      [addRow, deleteRow]
   );

   const table = useReactTable<SupplierTableRow>({
      data: data,
      defaultColumn,
      columns,
      getCoreRowModel: getCoreRowModel(),
   });

   // Expose our state to non-React parts of the page. This isn't ideal,
   // but it swaps in well with our `wiki_edit.js` logic.
   window.WikiSuppliers = {
      getFormValues: () =>
         data
            .filter(
               row =>
                  // We don't want to grab the bottom row of the table that's just
                  // and interactive way to add new rows.
                  row.action !== 'add'
            )
            .map(row => ({
               'part_#': row['part_#'],
               supplier: row.supplier,
               type: row.type,
               url: row.url,
            })),
   };

   return (
      <Stack spacing="20px">
         <TableContainer borderRadius="md" border="1px" borderColor={color.gray[300]} bg="white">
            <Table>
               <Thead>
                  {table.getHeaderGroups().map(headerGroup => (
                     <Tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                           <Th pl={space[3]} key={header.id}>
                              {header.isPlaceholder
                                 ? null
                                 : flexRender(header.column.columnDef.header, header.getContext())}
                           </Th>
                        ))}
                     </Tr>
                  ))}
               </Thead>
               <Tbody>
                  {table.getRowModel().rows.map(row => (
                     <Tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                           <Td
                              key={cell.id}
                              className={cell.column.id + '-column'}
                              w={cell.column.id === 'actions' ? '0px' : undefined}
                              pl={space[3]}
                              pr={space[1]}
                              maxWidth={cell.column.id === 'url' ? '200px' : '80px'}
                           >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                           </Td>
                        ))}
                     </Tr>
                  ))}
               </Tbody>
            </Table>
         </TableContainer>
         <Stack spacing={0} borderRadius="md" border="1px" borderColor={color.gray[300]} bg="white">
            <Textarea
               border="none"
               placeholder={_js('Paste newline-delimited URLs here...')}
               value={bulkAddText}
               onChange={e => setBulkAddText(e.target.value)}
            />
            <Button onClick={bulkAdd} isDisabled={bulkAddText.trim() === ''}>
               {_js('Bulk Add')}
            </Button>
         </Stack>
      </Stack>
   );
}

initializeChakraComponent('SuppliersEdit', SuppliersEdit);
